import {
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import { holdsNoAccess } from "@/contexts/sessionAccess";
import { isRequestCanceled } from "@/services/apiErrors";
import {
  createUiStorageScope,
  type UiStorageScope,
} from "@/storage/uiStorageScope";
import {
  loadScopedUiEntities,
  type ScopedUiEntityResource,
} from "@/storage/scopedUiEntityLoader";

export interface ScopedUiParams {
  isAuthenticated: boolean;
  userId?: string | null;
  projectId?: string | null;
  // What the account is allowed to read. Given by a caller that syncs the
  // collections, so an account granted nothing never asks for them.
  permissions?: readonly string[];
}

interface ScopedUiCollectionOptions<TRecord> {
  emptySnapshot: readonly TRecord[];
  getSnapshot: (scopeKey: string | null) => readonly TRecord[];
  params: ScopedUiParams;
  subscribe: (
    scopeKey: string,
    onStoreChange: () => void,
  ) => () => void;
}

export type ScopedUiSyncResource<TRecord = unknown> =
  ScopedUiEntityResource<TRecord>;

export interface ScopedUiSyncTask {
  run: (
    scope: UiStorageScope,
    abortController: AbortController,
    getIgnoreResult: () => boolean,
  ) => Promise<void>;
}

export function useScopedUiCollection<TRecord>({
  emptySnapshot,
  getSnapshot,
  params,
  subscribe,
}: ScopedUiCollectionOptions<TRecord>): readonly TRecord[] {
  const scope = useScopedUiScope(params);
  const subscribeToScope = useCallback(
    (onStoreChange: () => void) => (
      scope
        ? subscribe(scope.key, onStoreChange)
        : () => undefined
    ),
    [scope, subscribe],
  );
  const readSnapshot = useCallback(
    () => getSnapshot(scope?.key ?? null),
    [getSnapshot, scope],
  );

  return useSyncExternalStore(
    subscribeToScope,
    readSnapshot,
    () => emptySnapshot,
  );
}

export function useScopedUiSync(
  params: ScopedUiParams,
  resources: readonly ScopedUiSyncTask[],
): void {
  const scope = useScopedUiScope(params);
  // An account holding no access is refused both collections, so it is never
  // asked for them: it syncs nothing rather than spending two requests on a
  // pair of answers it may not read.
  const canRead = (
    params.isAuthenticated
    && !holdsNoAccess(params.projectId, params.permissions ?? [])
  );

  useUiSyncForScope(canRead ? scope : null, resources);
}

/**
 * Reads a scope that is already known, rather than one built from a session.
 *
 * The pages a visitor reads without signing in belong to no account, so they
 * are synced into a scope of their own whether or not anybody is signed in.
 */
export function useUiSyncForScope(
  scope: UiStorageScope | null,
  resources: readonly ScopedUiSyncTask[],
): void {
  useEffect(() => {
    if (!scope) return;

    const abortController = new AbortController();
    let ignoreResult = false;

    resources.forEach((resource) => {
      void resource.run(scope, abortController, () => ignoreResult);
    });

    return () => {
      ignoreResult = true;
      abortController.abort();
    };
  }, [resources, scope]);
}

export function createScopedUiSyncTaskGroup(
  resources: readonly ScopedUiSyncResource[],
): ScopedUiSyncTask {
  return {
    run: (scope, abortController, getIgnoreResult) => (
      syncScopedResources({
        abortController,
        getIgnoreResult,
        resources,
        scope,
      })
    ),
  };
}

export function useScopedUiScope(
  params: Pick<ScopedUiParams, "projectId" | "userId">,
): UiStorageScope | null {
  return useMemo(
    () => createUiStorageScope({
      userId: params.userId,
      projectId: params.projectId,
    }),
    [params.userId, params.projectId],
  );
}

async function syncScopedResources({
  abortController,
  getIgnoreResult,
  resources,
  scope,
}: {
  abortController: AbortController;
  getIgnoreResult: () => boolean;
  resources: readonly ScopedUiSyncResource[];
  scope: UiStorageScope;
}): Promise<void> {
  try {
    await loadScopedUiEntities({
      resources,
      scopeKey: scope.key,
      signal: abortController.signal,
      shouldIgnore: getIgnoreResult,
    });
  } catch (err) {
    if (abortController.signal.aborted || isRequestCanceled(err)) return;
    if (getIgnoreResult()) return;
  }
}
